/**
 * MainDockBuilder.ts — constructs the legacy "main" dock attached
 * to the player's spawn position.
 *
 * The dock itself is currently invisible: with annotation-driven
 * docks lit up, `SPAWN_TEST_DOCK` is wired to `false` here, so we
 * skip the mesh instance and the collider AABB. The {@link Dock}
 * object is still created because the rest of the app binds the
 * mooring UI, pickup service and rope renderer to `dock.bollards`
 * (the player needs *some* anchor set even when no annotation
 * docks exist on the active terrain).
 *
 * The dead "spawn the test dock" code path stays here behind the
 * flag for the day we want a quick visual harness without messing
 * with authored terrain features.
 */
import { createDockMesh } from "../assets/ProceduralAssets";
import type { Mat4, StaticMeshPass } from "../passes/StaticMeshPass";
import type { Boat } from "./Boat";
import type { Aabb } from "./Collisions";
import { Dock, defaultDockSpec } from "./Dock";

/**
 * Flip to `true` to spawn + render + collide the legacy main dock
 * (placed 12 m abeam port of the player). Off because annotation
 * docks now provide the real layout.
 */
const SPAWN_TEST_DOCK = false;

/** Distance abeam to port where the dock would sit when enabled. */
const DOCK_OFFSET_M = 12;

export interface MainDockResult {
  /** The constructed dock (always non-null — used for bollard anchors). */
  dock: Dock;
}

export interface MainDockBuildArgs {
  playerBoat: Boat;
  staticMeshPass: StaticMeshPass;
  /** Push the dock's collider AABB here when {@link SPAWN_TEST_DOCK}. */
  dockAabbs: Aabb[];
  waterLevel: number;
}

/**
 * Build the main dock + (optionally) its render mesh and collider.
 * The returned `dock` is always populated so callers can iterate
 * `dock.bollards` for mooring UI even when the dock isn't visible.
 */
export function buildMainDock(args: MainDockBuildArgs): MainDockResult {
  const { playerBoat, staticMeshPass, dockAabbs, waterLevel } = args;
  const dockSpec = defaultDockSpec();
  // Place dock 12 m abeam (port side) of the player so the player
  // has space to drift in. Heading aligned with the player so the
  // long axis is parallel to the boat — a "side-to" approach.
  const dockHeading = playerBoat.heading;
  const fwdX = Math.sin(dockHeading);
  const fwdZ = -Math.cos(dockHeading);
  const sideX = -fwdZ;
  const sideZ = fwdX;
  const dock = new Dock(dockSpec, {
    posX: playerBoat.posX + sideX * -DOCK_OFFSET_M,
    posZ: playerBoat.posZ + sideZ * -DOCK_OFFSET_M,
    headingY: dockHeading,
    waterLevel,
  });
  // Single static dock mesh placed at world (deckTopY, posX, posZ,
  // headingY).
  const dockMeshId = "dock_main";
  staticMeshPass.addGroup(dockMeshId, createDockMesh(dockSpec, dockMeshId), 0, {
    castsHardShadow: true,
  });

  if (SPAWN_TEST_DOCK) {
    const cosY = Math.cos(dock.headingY);
    const sinY = Math.sin(dock.headingY);
    // prettier-ignore
    const m = new Float32Array([
      cosY,  0, sinY, 0,
      0,     1, 0,    0,
      -sinY, 0, cosY, 0,
      dock.posX, dock.deckTopY, dock.posZ, 1,
    ]) as Mat4;
    staticMeshPass.addInstance(dockMeshId, m);

    // Collision AABB for the dock deck. The dock is rotated about Y
    // by dockHeading; we wrap the rotated rectangle with a
    // world-aligned AABB (exact when heading is axis-aligned,
    // conservative otherwise).
    const halfL = dockSpec.length * 0.5;
    const halfW = dockSpec.width * 0.5;
    const corners: Array<[number, number]> = [
      [+halfL, +halfW],
      [+halfL, -halfW],
      [-halfL, +halfW],
      [-halfL, -halfW],
    ];
    let minX = Infinity,
      maxX = -Infinity,
      minZ = Infinity,
      maxZ = -Infinity;
    for (const [lx, lz] of corners) {
      const wx = dock.posX + cosY * lx + sinY * lz;
      const wz = dock.posZ - sinY * lx + cosY * lz;
      minX = Math.min(minX, wx);
      maxX = Math.max(maxX, wx);
      minZ = Math.min(minZ, wz);
      maxZ = Math.max(maxZ, wz);
    }
    const yTop = dock.deckTopY;
    const yBot = waterLevel - dockSpec.pileDepth;
    dockAabbs.push({
      min: [minX, yBot, minZ],
      max: [maxX, yTop, maxZ],
    });
  }

  return { dock };
}
